import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Collapse,
  Group,
  Pagination,
  Pill,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useCallback, useMemo, useState } from 'react';
import { PiCaretDownBold, PiCaretUpBold, PiDownload } from 'react-icons/pi';
import { useLocation } from 'wouter';
import CreateProposal from '@/components/commercial-proposal/CreateProposal/CreateProposal';
import DateSinceDisplay from '@/components/common/DateSinceDisplay';
import {
  GenericTable,
  GenericTableColumn,
} from '@/components/GenericTable/GenericTable';
import JurisdictionsSelect from '@/components/JurisdictionsSelect/JurisdictionsSelect';
import ServiceSelector from '@/components/service-selector/ServiceSelector';
import { ClearFiltersButton } from '@/components/ClearFiltersButton';
import { useCommercialProposalsFilters } from '@/hooks/useCommercialProposalsFilters';
import { useUrlDraft } from '@/hooks/useUrlState';
import { semanticJurisdiction } from '@/lib/helpers';
import {
  TCommercialProposal,
  useListCommercialProposals,
} from '@/models/commercial-proposal';
import {
  PERMISSIONS,
  useHasPermission,
  useProposalAccess,
} from '@/models/permissions';
import { TService, useServices } from '@/models/services';

export default function CommercialProposalListPage() {
  const [_, nav] = useLocation();
  const { canView, shopOnly, canSeeShop } = useProposalAccess();
  const { state, filters, setState, clearFilters, isFiltered } =
    useCommercialProposalsFilters(canSeeShop);

  const [searchInput, setSearchInput] = useUrlDraft(state.term, (term) =>
    setState({ term }),
  );
  const [blockInput, setBlockInput] = useUrlDraft(
    state.blockTitle,
    (blockTitle) => setState({ blockTitle }),
    { normalize: (next) => next.trim() },
  );

  const [filtersOpened, setFiltersOpened] = useState(true);
  const [serviceSelectorOpened, setServiceSelectorOpened] = useState(false);
  const [downloadingIds, setDownloadingIds] = useState<string[]>([]);

  const selectedServiceIds = useMemo(
    () => filters.serviceIds ?? [],
    [filters.serviceIds],
  );
  const { data: selectedServices } = useServices(selectedServiceIds);

  const canEdit = useHasPermission(PERMISSIONS.PROPOSALS_WRITE);

  const { data, isLoading, error } = useListCommercialProposals(filters);

  const addService = (service: TService) => {
    if (selectedServiceIds.includes(service.id)) return;
    setState({ serviceIds: [...selectedServiceIds, service.id] });
  };

  const removeService = (serviceId: number) => {
    setState({
      serviceIds: selectedServiceIds.filter((id) => id !== serviceId),
    });
  };

  const handleSortChange = (field: string) => {
    setState(
      {
        sortBy: field as typeof state.sortBy,
        sortOrder:
          state.sortBy === field && state.sortOrder === 'desc' ? 'asc' : 'desc',
      },
      { resetPage: false },
    );
  };

  const downloadPdf = useCallback(async (uuid: string) => {
    setDownloadingIds((prev) => {
      if (prev.includes(uuid)) return prev;
      return [...prev, uuid];
    });
    try {
      window.open(
        `/api/backoffice/commercial-proposal/download-pdf?uuid=${uuid}`,
        '_blank',
      );
    } finally {
      setTimeout(() => {
        setDownloadingIds((prev) => prev.filter((id) => id !== uuid));
      }, 1000);
    }
  }, []);

  const columns = useMemo<GenericTableColumn<TCommercialProposal>[]>(
    () => [
      {
        accessor: 'name',
        title: 'Name',
        sortable: true,
        render: (proposal) => (
          <Stack gap={0}>
            <Group gap={4}>
              <Text size="xs" fw={500}>
                {proposal.name}
              </Text>
              <Text size="xs" c="dimmed">
                ID: {proposal.id}
              </Text>
              {proposal.isDraft && (
                <Badge size="xs" variant="light" color="gray">
                  Draft
                </Badge>
              )}
              {canSeeShop && proposal.shopEnabled && (
                <Badge size="xs" variant="light" color="teal">
                  Shop
                </Badge>
              )}
            </Group>
          </Stack>
        ),
      },
      {
        accessor: 'jurisdiction',
        title: 'Jurisdiction',
        sortable: true,
        width: 150,
        render: (proposal) => (
          <Text size="xs">{semanticJurisdiction(proposal.jurisdiction)}</Text>
        ),
      },
      {
        accessor: 'currency',
        title: 'Currency',
        sortable: true,
        width: 100,
        render: (proposal) => <Text size="xs">{proposal.currency}</Text>,
      },
      {
        accessor: 'updatedAt',
        title: 'Updated',
        sortable: true,
        width: 150,
        render: (proposal) => (
          <DateSinceDisplay date={proposal.updatedAt} size="xs" c="dimmed" />
        ),
      },
      {
        accessor: 'actions' as any,
        title: 'Actions',
        sortable: false,
        width: 100,
        render: (proposal) => (
          <Group gap="xs" wrap="nowrap">
            {canView && (
              <ActionIcon
                variant="subtle"
                size="xs"
                onClick={(e) => {
                  e.stopPropagation();
                  downloadPdf(proposal.uuid);
                }}
                loading={downloadingIds.includes(proposal.uuid)}
              >
                <PiDownload size={16} />
              </ActionIcon>
            )}
          </Group>
        ),
      },
    ],
    [canView, canSeeShop, downloadingIds, downloadPdf],
  );

  return (
    <Stack gap="md" p="md">
      <Group justify="space-between">
        <Button
          variant="subtle"
          size="xs"
          onClick={() => setFiltersOpened(!filtersOpened)}
          rightSection={filtersOpened ? <PiCaretUpBold /> : <PiCaretDownBold />}
        >
          {filtersOpened ? 'Hide Filters' : 'Show Filters'}
        </Button>
        {canEdit && <CreateProposal onCreated={(id) => nav(`/edit/${id}`)} />}
      </Group>

      {/* Filters */}
      <Collapse expanded={filtersOpened}>
        <Stack gap="sm">
          <SimpleGrid
            cols={{ base: 1, xs: 2, sm: 3, md: 4 }}
            spacing="sm"
            verticalSpacing="sm"
          >
            <TextInput
              label="Search"
              placeholder="Search by name"
              value={searchInput}
              onChange={(e) => setSearchInput(e.currentTarget.value)}
              size="xs"
            />

            <JurisdictionsSelect
              value={filters.jurisdiction}
              onChange={(value) => setState({ jurisdiction: value ?? '' })}
            />

            <Select
              label="Currency"
              placeholder="All"
              value={state.currency || null}
              onChange={(value) => setState({ currency: value ?? '' })}
              data={['EUR', 'USD', 'GBP']}
              clearable
              size="xs"
            />

            <Select
              label="Status"
              placeholder="All"
              value={
                state.isDraft === ''
                  ? null
                  : state.isDraft === 'true'
                    ? 'draft'
                    : 'published'
              }
              onChange={(value) =>
                setState({
                  isDraft:
                    value === 'draft'
                      ? 'true'
                      : value === 'published'
                        ? 'false'
                        : '',
                })
              }
              data={[
                { label: 'Draft', value: 'draft' },
                { label: 'Published', value: 'published' },
              ]}
              clearable
              size="xs"
            />

            {canSeeShop && (
              <Tooltip
                label="You can see shop proposals only"
                disabled={!shopOnly}
              >
                <Switch
                  label="Only shop-enabled"
                  checked={shopOnly || state.shopEnabled}
                  disabled={shopOnly}
                  onChange={(e) =>
                    setState({ shopEnabled: e.currentTarget.checked })
                  }
                  size="xs"
                  mt={{ base: 0, xs: 22 }}
                />
              </Tooltip>
            )}

            <TextInput
              label="Contains blocks"
              placeholder="substance, statutory"
              description="Comma-separated. Each part must match a block title."
              value={blockInput}
              onChange={(e) => setBlockInput(e.currentTarget.value)}
              size="xs"
            />
          </SimpleGrid>

          <Stack gap={6}>
            <Group gap="xs" align="center">
              <Text size="xs" fw={500}>
                Includes services
              </Text>
              <Button
                variant="light"
                size="compact-xs"
                onClick={() => setServiceSelectorOpened(true)}
              >
                Select services
              </Button>
              {selectedServiceIds.length > 1 && (
                <Text size="xs" c="dimmed">
                  All of them must be present
                </Text>
              )}
            </Group>

            {selectedServiceIds.length > 0 && (
              <Group gap={6}>
                {selectedServiceIds.map((id) => {
                  const service = selectedServices?.find((s) => s?.id === id);
                  return (
                    <Pill
                      key={id}
                      size="sm"
                      withRemoveButton
                      onRemove={() => removeService(id)}
                    >
                      {service?.name.trim() || `Service #${id}`}
                    </Pill>
                  );
                })}
              </Group>
            )}
          </Stack>

          {isFiltered && (
            <Group justify="flex-end">
              <ClearFiltersButton visible onClick={clearFilters} />
            </Group>
          )}
        </Stack>
      </Collapse>

      {/*
        No jurisdiction/currency defaults are passed: the drawer falls back to
        them after a clear and disables the currency control when supplied,
        which would narrow discovery - especially under name matching.
      */}
      <ServiceSelector
        opened={serviceSelectorOpened}
        onClose={() => setServiceSelectorOpened(false)}
        exclude={selectedServiceIds}
        onSelect={addService}
      />

      {/* Error state */}
      {error && (
        <Text c="red" size="sm">
          Error loading proposals:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </Text>
      )}

      {/* Debug info */}
      {!isLoading && !error && (
        <Text size="xs" c="dimmed">
          Total records: {data?.total || 0} | Proposals:{' '}
          {data?.proposals?.length || 0}
        </Text>
      )}

      {/* Table */}
      <GenericTable<TCommercialProposal>
        records={data?.proposals || []}
        columns={columns}
        loading={isLoading}
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
        onSort={handleSortChange}
        emptyMessage="No proposals found"
        idAccessor="id"
        onRowClick={(proposal) => {
          if (canEdit) {
            nav(`/edit/${(proposal as TCommercialProposal).id}`);
          } else if (canView) {
            nav(`/view/${(proposal as TCommercialProposal).id}`);
          }
        }}
      />

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <Center>
          <Pagination
            total={data.totalPages}
            value={filters.page || 1}
            onChange={(page) => setState({ page })}
            size="sm"
          />
        </Center>
      )}

      {/* Summary */}
      {data && (
        <Text size="xs" c="dimmed">
          Showing {data.proposals.length} of {data.total} proposals (Page{' '}
          {data.page} of {data.totalPages})
        </Text>
      )}
    </Stack>
  );
}
